import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Plus, Pencil, Check, Users, Wallet, Ban, Home as HomeIcon, PawPrint, MapPin, ChefHat, CalendarDays } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import MobileShell from "../components/MobileShell";
import { MillieAvatar } from "../components/MascotBubble";
import HouseholdForm from "../components/HouseholdForm";
import CalendarConnect from "../components/CalendarConnect";
import FamilySharing from "../components/FamilySharing";
import RewardsWallet from "../components/RewardsWallet";
import NotificationSettings from "../components/NotificationSettings";
import PlanCard from "../components/PlanCard";
import MillieStudio from "../components/MillieStudio";

export default function Profile() {
  const { user, households, activeId, activeHousehold, selectHousehold, loadHouseholds, logout } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("view"); // view | edit | create
  const [loading, setLoading] = useState(false);

  const saveEdit = async (payload) => {
    setLoading(true);
    try {
      await api.put(`/households/${activeId}`, payload);
      await loadHouseholds();
      toast.success("Household updated. Re-plan to freshen up your meals!");
      setMode("view");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const createNew = async (payload) => {
    setLoading(true);
    try {
      const { data } = await api.post("/households", payload);
      await loadHouseholds();
      selectHousehold(data.id);
      await api.post("/plan/generate", { household_id: data.id });
      toast.success("New household created & planned!");
      setMode("view");
      navigate("/plan");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setLoading(false);
    }
  };

  if (mode === "edit") {
    return (
      <MobileShell>
        <div className="px-6 pt-10"><h1 className="font-heading text-2xl font-bold text-sorted-ink">Edit household</h1></div>
        <HouseholdForm initial={activeHousehold} onSubmit={saveEdit} loading={loading} submitLabel="Save changes" />
      </MobileShell>
    );
  }
  if (mode === "create") {
    return (
      <MobileShell>
        <div className="px-6 pt-10"><h1 className="font-heading text-2xl font-bold text-sorted-ink">New household</h1></div>
        <HouseholdForm onSubmit={createNew} loading={loading} submitLabel="Create & plan" />
      </MobileShell>
    );
  }

  return (
    <MobileShell>
      <div className="px-6 pt-12">
        <div className="flex items-center gap-4">
          <MillieAvatar size={64} float={false} />
          <div>
            <h1 className="font-heading text-2xl font-bold text-sorted-ink" data-testid="profile-name">{user?.name}</h1>
            <p className="text-sorted-slate font-semibold text-sm">{user?.email}</p>
          </div>
        </div>
      </div>

      {/* Household switcher */}
      <div className="px-6 mt-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading text-lg font-semibold text-sorted-ink">Households</h2>
          <button onClick={() => setMode("create")} data-testid="add-household"
            className="flex items-center gap-1 text-sm font-bold text-sorted-ink bg-sorted-blush px-3 py-1.5 rounded-full">
            <Plus size={15} strokeWidth={2.5} /> New
          </button>
        </div>
        <div className="space-y-2">
          {households.map((h) => (
            <button key={h.id} onClick={() => selectHousehold(h.id)} data-testid={`household-${h.id}`}
              className={`w-full flex items-center gap-3 p-4 rounded-3xl border-2 text-left transition-colors ${h.id === activeId ? "bg-white border-sorted-peach shadow-soft" : "bg-white/60 border-[#FFF3F0]"}`}>
              <span className="w-10 h-10 rounded-full bg-sorted-sage flex items-center justify-center text-sorted-ink"><HomeIcon size={18} strokeWidth={2.5} /></span>
              <span className="flex-1">
                <span className="font-heading font-semibold text-sorted-ink block">{h.name}</span>
                <span className="text-xs text-sorted-slate font-semibold">{h.members?.length || 0} people · {h.currency}{h.budget_weekly}/wk{h.is_owner === false ? " · shared with you" : h.shared_count ? ` · shared with ${h.shared_count}` : ""}</span>
              </span>
              {h.id === activeId && <Check size={20} strokeWidth={3} className="text-sorted-peach" />}
            </button>
          ))}
        </div>
      </div>

      {/* Active household detail */}
      {activeHousehold && (
        <div className="px-6 mt-8">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading text-lg font-semibold text-sorted-ink">{activeHousehold.name}</h2>
            <button onClick={() => setMode("edit")} data-testid="edit-household"
              className="flex items-center gap-1 text-sm font-bold text-sorted-slate">
              <Pencil size={14} strokeWidth={2.5} /> Edit
            </button>
          </div>
          <div className="bg-white rounded-3xl border-2 border-[#FFF3F0] shadow-soft divide-y divide-[#FFF3F0]">
            <div className="p-4 flex items-center gap-3">
              <Users size={18} strokeWidth={2.5} className="text-sorted-slate" />
              <span className="flex-1 text-sm font-semibold text-sorted-ink">
                {activeHousehold.members?.map((m) => m.name).join(", ") || "No members"}
              </span>
            </div>
            <div className="p-4 flex items-center gap-3">
              <Wallet size={18} strokeWidth={2.5} className="text-sorted-slate" />
              <span className="flex-1 text-sm font-semibold text-sorted-ink">{activeHousehold.currency}{activeHousehold.budget_weekly} weekly budget</span>
            </div>
            <div className="p-4 flex items-center gap-3">
              <Ban size={18} strokeWidth={2.5} className="text-sorted-slate" />
              <span className="flex-1 text-sm font-semibold text-sorted-ink">
                {[...(activeHousehold.allergies || []), ...(activeHousehold.dietary_requirements || [])].join(", ") || "No allergies or dietary needs"}
              </span>
            </div>
            {activeHousehold.pets?.length > 0 && (
              <div className="p-4 flex items-center gap-3">
                <PawPrint size={18} strokeWidth={2.5} className="text-sorted-slate" />
                <span className="flex-1 text-sm font-semibold text-sorted-ink">{activeHousehold.pets.map((p) => p.name || p.type).join(", ")}</span>
              </div>
            )}
            <div className="p-4 flex items-center gap-3">
              <CalendarDays size={18} strokeWidth={2.5} className="text-sorted-slate" />
              <span className="flex-1 text-sm font-semibold text-sorted-ink">{activeHousehold.meals_per_week ?? 7} dinners/wk{activeHousehold.include_lunches ? " + lunches" : ""}{activeHousehold.include_breakfasts ? " + brekkie" : ""}</span>
            </div>
            <div className="p-4 flex items-center gap-3">
              <ChefHat size={18} strokeWidth={2.5} className="text-sorted-slate" />
              <span className="flex-1 text-sm font-semibold text-sorted-ink capitalize">{activeHousehold.cooking_skill || "confident"} cook · {(activeHousehold.appliances || []).join(", ") || "standard kitchen"}</span>
            </div>
            <div className="p-4 flex items-center gap-3">
              <MapPin size={18} strokeWidth={2.5} className="text-sorted-slate" />
              <span className="flex-1 text-sm font-semibold text-sorted-ink" data-testid="profile-shops">
                {(activeHousehold.supermarkets || []).join(", ") || "No shops picked yet"}{activeHousehold.suburb ? ` · ${activeHousehold.suburb}` : ""}
              </span>
            </div>
          </div>
        </div>
      )}

      {activeHousehold && <div className="px-6 mt-8"><FamilySharing /></div>}
      <div className="px-6 mt-8"><PlanCard /></div>
      {activeHousehold && <div className="px-6 mt-8"><CalendarConnect /></div>}
      <div className="px-6 mt-8"><NotificationSettings /></div>
      <div className="px-6 mt-8"><RewardsWallet /></div>
      <div className="px-6 mt-8"><MillieStudio /></div>

      <div className="px-6 mt-8" data-testid="account-section">
        <h2 className="font-hand text-2xl text-sorted-ink mb-2">Account</h2>
        <div className="bg-white rounded-3xl border-2 border-[#FDECEF] p-4 shadow-soft">
          <p className="text-sm font-semibold text-sorted-ink" data-testid="account-email">{user?.email}</p>
          <p className="text-[11px] text-sorted-slate font-semibold">Signed in{user?.name ? ` as ${user.name}` : ""}</p>
          <button onClick={logout} data-testid="logout-btn"
            className="mt-3 w-full flex items-center justify-center gap-2 text-warning font-bold py-3.5 rounded-full bg-white border-2 border-[#FFF3F0]">
            <LogOut size={18} strokeWidth={2.5} /> Log out
          </button>
        </div>
      </div>
    </MobileShell>
  );
}
